/**
 * A bare printable key over the list goes into the SEARCH FIELD, which is what a trainer typing
 * over a list of names means by it.
 *
 * ── What this replaces, and why it was a defect ──────────────────────────────────────────────
 * `N` used to open the create dialog from `document`, guarded only against editable targets. A
 * trainer who clicked the list and typed to filter — the ordinary thing to do on a screen whose
 * whole toolbar is a search box — lost their keystrokes into a wizard, and nothing on the screen
 * advertised the shortcut: `web:planLibrary.empty.shortcut` had no call site. Neither half of that
 * bargain held, so the shortcut is gone and the keys go where the trainer was aiming.
 *
 * The three guards that decide when a letter is NOT this:
 *
 * · **A field already has it.** Checked by the target's editability rather than by a tag list, so a
 *   future `contenteditable` or `<select>` is covered without this file learning about it.
 * · **A modifier is down.** `Cmd+N`, `Ctrl+F`, `Alt+D` all belong to the browser.
 * · **Something has already handled it.** `defaultPrevented` covers the list's own arrow keys and
 *   anything a dialog above the screen consumed; `enabled` covers a create dialog being open at
 *   all, which is also when Base UI's focus trap keeps the event away from here.
 *
 * ── One character, and the browser types it ──────────────────────────────────────────────────
 * `key.length === 1` is the whole test for printable: every named key (`Enter`, `ArrowDown`, `F5`,
 * `Escape`) has a longer name. A space is excluded because it scrolls a page and activates a
 * control, and a trainer never starts a search with one.
 *
 * The character is NOT inserted here and the event is NOT prevented. Focus moves during `keydown`,
 * before the default action runs, so the browser puts the letter into the field it has just moved
 * to — which also means the selection, the composition and the undo stack are the platform's rather
 * than something this file maintains.
 */

import { useEffect, type RefObject } from 'react';

/** Is the event heading somewhere a letter already means a letter? */
function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

export function useTypeToSearch(
  field: RefObject<HTMLInputElement | null>,
  enabled: boolean,
): void {
  useEffect(() => {
    if (!enabled) return undefined;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.key.length !== 1 || event.key === ' ') return;
      if (isTypingTarget(event.target)) return;
      const input = field.current;
      if (input === null || input.disabled) return;
      input.focus();
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [field, enabled]);
}
